import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function toDate(v) {
  if (!v) return undefined;
  const d = parse(v, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

/**
 * Calendar-popover date picker that is a drop-in for <input type="date">.
 * value/onChange use "YYYY-MM-DD" strings so existing state handlers keep working.
 */
export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = "Pick a date",
  disabled = false,
  className,
  fromYear = 1975,
  toYear = new Date().getFullYear() + 3,
  quickPicks = false,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const selected = toDate(value);
  const minD = toDate(min);
  const maxD = toDate(max);
  const matchers = [];
  if (minD) matchers.push({ before: minD });
  if (maxD) matchers.push({ after: maxD });

  const pick = (d) => { onChange(format(d, "yyyy-MM-dd")); setOpen(false); };
  const today = new Date();
  const tomorrow = new Date(Date.now() + 86_400_000);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={rest["data-testid"]}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 opacity-70 shrink-0" />
          {selected ? format(selected, "dd MMM yyyy") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {quickPicks && (
          <div className="flex items-center gap-1.5 p-2 border-b border-border">
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => pick(today)} data-testid="datepicker-today">Today</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => pick(tomorrow)} data-testid="datepicker-tomorrow">Tomorrow</Button>
          </div>
        )}
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear}
          classNames={{
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "hidden",
            caption_dropdowns: "flex gap-1.5 items-center",
            nav: "hidden",
            vhidden: "hidden",
            dropdown: "rounded-md border border-input bg-background px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring",
          }}
          disabled={matchers.length ? matchers : undefined}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
